import React, { useState, useEffect, useRef, useCallback } from "react";
import {
    Box, Typography, Breadcrumbs, Link, Button, CircularProgress, LinearProgress,
    Paper, Stack, Dialog, DialogTitle, DialogContent, DialogActions,
    TextField, Divider, Snackbar, Alert
} from "@mui/material";
import CreateNewFolderIcon from "@mui/icons-material/CreateNewFolder";
import DriveFileRenameOutlineIcon from "@mui/icons-material/DriveFileRenameOutline";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import CloudOutlinedIcon from "@mui/icons-material/CloudOutlined";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { useSearchParams } from "react-router-dom";
import { fetchDriveFolder, createFolder, uploadFiles, deleteItem, renameItem, moveItem } from "../services/drive.service";
import FileTable from "../components/FileExplorer/FileTable";
import LinkOneDriveFolderDialog from "../components/FileExplorer/LinkOneDriveFolderDialog";
import MoveItemModal from "../components/FileExplorer/MoveItemModal";
import FilePreviewModal from "../components/FileExplorer/FilePreviewModal";
import ConfirmDeleteModal from "../components/ConfirmDeleteModal";
import Container from "../base/Container";
import { rememberSectionPath } from "../hooks/useSessionState";
import { useFileChangeListener, useFileUploadRequestListener } from "../hooks/useFileChangeListener";
import { openFileInBrowser } from "../utils/openFileInBrowser";

export default function FileSharePage() {
    const [searchParams, setSearchParams] = useSearchParams();

    // currentFolderId is fully driven by the URL — browser back/forward works automatically
    const currentFolderId = searchParams.get("folder") || "org-true-root";

    const [items, setItems] = useState<any[]>([]);
    const [breadcrumbs, setBreadcrumbs] = useState<any[]>([]);
    const [currentFolder, setCurrentFolder] = useState<any>(null);
    const [isLoading, setIsLoading] = useState<boolean>(true);

    const [isNewFolderModalOpen, setIsNewFolderModalOpen] = useState(false);
    const [newFolderName, setNewFolderName] = useState("");
    const [newFolderNameError, setNewFolderNameError] = useState<string | null>(null);
    const [isCreatingFolder, setIsCreatingFolder] = useState(false);
    const [isUploading, setIsUploading] = useState(false);
    const [uploadError, setUploadError] = useState<string | null>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [isPreviewModalOpen, setIsPreviewModalOpen] = useState(false);
    const [previewItem, setPreviewItem] = useState<any>(null);
    const [isLinkOneDriveOpen, setIsLinkOneDriveOpen] = useState(false);

    /**
     * Every item under a client's folder carries its clientId, so anywhere at
     * or below one the dialog needs no picker. Higher up — Home, the Clients
     * folder — there is genuinely no client in scope and the dialog asks.
     */
    const folderClientId: string | undefined =
        currentFolder?.prospoContext?.clientId || undefined;

    const [selectedItem, setSelectedItem] = useState<any>(null);
    const [isRenameModalOpen, setIsRenameModalOpen] = useState(false);
    const [renameValue, setRenameValue] = useState("");
    const [isRenamingItem, setIsRenamingItem] = useState(false);
    const [isMoveModalOpen, setIsMoveModalOpen] = useState(false);
    const [deleteTarget, setDeleteTarget] = useState<any>(null);

    // Duplicate file confirmation
    const [duplicateFiles, setDuplicateFiles] = useState<File[]>([]);
    const [pendingFiles, setPendingFiles] = useState<File[]>([]);
    const [isDuplicateDialogOpen, setIsDuplicateDialogOpen] = useState(false);

    // Single navigation function — keeps URL and state in sync
    const navigateToFolder = (id: string) => {
        if (id === "org-true-root") {
            setSearchParams({});
        } else {
            setSearchParams({ folder: id });
        }
    };

    const fetchFolderData = async (folderId: string) => {
        setIsLoading(true);
        try {
            const response: any = await fetchDriveFolder(folderId === "org-true-root" ? undefined : folderId);

            if (response && response.hasError === false) {
                setItems(response.items || []);
                const crumbs = response.breadcrumbs || [];
                const homecrumb = { _id: "org-true-root", name: "Home" };
                setBreadcrumbs(folderId === "org-true-root" ? crumbs : [homecrumb, ...crumbs]);
                setCurrentFolder(response.currentFolder || null);
            } else {
                // Invalid/missing folder — fall back to root
                if (folderId !== "org-true-root") navigateToFolder("org-true-root");
            }
        } catch (error) {
            console.error("Failed to fetch folder contents:", error);
            // Invalid folderId in URL — redirect to root
            if (folderId !== "org-true-root") navigateToFolder("org-true-root");
        } finally {
            setIsLoading(false);
        }
    };

    // Fires whenever the URL ?folder param changes (including browser back/forward)
    useEffect(() => {
        fetchFolderData(currentFolderId);
    }, [currentFolderId]);

    // Reflect AI-agent-driven rename/move/delete without requiring a manual refresh.
    useFileChangeListener(useCallback(() => { fetchFolderData(currentFolderId); }, [currentFolderId]));

    // Agent-requested upload: open the same native file picker the "Upload File" button uses.
    useFileUploadRequestListener(useCallback(() => { fileInputRef.current?.click(); }, []));

    // So the sidebar "Files" nav link can return here instead of resetting to root.
    useEffect(() => {
        rememberSectionPath(
            "files",
            currentFolderId === "org-true-root" ? "/files" : `/files?folder=${currentFolderId}`
        );
    }, [currentFolderId]);

    const handleCreateFolder = async () => {
        if (!newFolderName.trim() || newFolderNameError) return;
        setIsCreatingFolder(true);
        try {
            const parentId = currentFolderId !== "org-true-root" ? currentFolderId : undefined;
            await createFolder(parentId, newFolderName);
            setIsNewFolderModalOpen(false);
            setNewFolderName("");
            setNewFolderNameError(null);
            fetchFolderData(currentFolderId);
        } catch (error) {
            alert("Failed to create folder. It might already exist.");
        } finally {
            setIsCreatingFolder(false);
        }
    };

    const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const files = event.target.files;
        if (!files || files.length === 0) return;

        // Convert to array FIRST — resetting the input clears the FileList reference
        const fileArray = Array.from(files);
        if (fileInputRef.current) fileInputRef.current.value = "";

        const oversized = fileArray.find(f => f.size > 100 * 1024 * 1024);
        if (oversized) {
            setUploadError(`"${oversized.name}" exceeds the 100 MB file size limit.`);
            return;
        }

        const existingNames = new Set(items.filter(i => !i.isFolder).map((i: any) => i.name));
        const dupes = fileArray.filter(f => existingNames.has(f.name));

        if (dupes.length > 0) {
            setDuplicateFiles(dupes);
            setPendingFiles(fileArray);
            setIsDuplicateDialogOpen(true);
            return;
        }

        await executeUpload(fileArray);
    };

    const executeUpload = async (fileArray: File[]) => {
        setIsUploading(true);
        const formData = new FormData();
        fileArray.forEach((file) => formData.append("files", file));
        formData.append("fileType", "General");
        formData.append("fileName", fileArray[0].name);
        formData.append("folderId", currentFolderId);
        try {
            await uploadFiles(formData);
            fetchFolderData(currentFolderId);
        } catch (error: any) {
            setUploadError(error?.message || "Failed to upload file.");
        } finally {
            setIsUploading(false);
        }
    };

    /**
     * Accepts the OneDrive actions so the signature matches FileTable's, which
     * gained them when linking shipped — without them this call site was a type
     * error. They are inert here: unlink and sync are offered only at a client
     * root, and this page never passes isAtClientRoot.
     */
    const handleItemAction = (
        action: 'rename' | 'move' | 'delete' | 'preview' | 'openInBrowser' | 'unlinkOneDrive' | 'syncOneDrive',
        item: any,
    ) => {
        setSelectedItem(item);
        if (action === 'preview') {
            setPreviewItem(item);
            setIsPreviewModalOpen(true);
        } else if (action === 'openInBrowser') {
            if (!openFileInBrowser(item)) alert("Unable to open file in browser.");
        } else if (action === 'rename') {
            setRenameValue(item.name);
            setIsRenameModalOpen(true);
        } else if (action === 'move') {
            setIsMoveModalOpen(true);
        } else if (action === 'delete') {
            setDeleteTarget(item);
        }
    };

    const executeDelete = async (item: any) => {
        if (!item) return;
        setDeleteTarget(null);
        try {
            await deleteItem(item._id);
            fetchFolderData(currentFolderId);
        } catch (error) {
            alert("Failed to delete item.");
        }
    };

    const executeRename = async () => {
        if (!renameValue.trim() || !selectedItem) return;
        setIsRenamingItem(true);
        try {
            await renameItem(selectedItem._id, renameValue);
            setIsRenameModalOpen(false);
            fetchFolderData(currentFolderId);
        } catch (error) {
            alert("Failed to rename item.");
        } finally {
            setIsRenamingItem(false);
        }
    };

    const executeMove = async (targetId: string) => {
        if (!targetId || !selectedItem) return;
        try {
            await moveItem(selectedItem._id, targetId);
            setIsMoveModalOpen(false);
            fetchFolderData(currentFolderId);
        } catch (error) {
            alert("Failed to move item.");
        }
    };

    return (
        <Container>
            <Box sx={{ width: "100%", height: "calc(100vh - 118px)", display: "flex", flexDirection: "column", gap: 2, overflow: "hidden" }}>
                {/* HEADER */}
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexShrink: 0 }}>
                    <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />}>
                        {breadcrumbs.map((crumb, index) => {
                            const isLast = index === breadcrumbs.length - 1;
                            return isLast ? (
                                <Typography key={crumb._id} color="text.primary" sx={{ fontWeight: 600 }}>
                                    {crumb.name}
                                </Typography>
                            ) : (
                                <Link key={crumb._id} underline="hover" color="inherit" component="button" variant="body1"
                                    onClick={() => navigateToFolder(crumb._id)}>
                                    {crumb.name}
                                </Link>
                            );
                        })}
                    </Breadcrumbs>
                    <Stack direction="row" spacing={2}>
                        {currentFolder?.webUrl && (
                            <Button
                                variant="outlined"
                                startIcon={<OpenInNewIcon />}
                                onClick={() => window.open(currentFolder.webUrl, "_blank")}
                            >
                                Open in SharePoint
                            </Button>
                        )}
                        {/* Same action as the client Files view, which reaches
                            the very same folders by a different route — it was
                            offered there and not here, so whether you could
                            link depended on how you had navigated. Available at
                            every level: the dialog asks which client when the
                            current folder does not imply one. */}
                        <Button
                            variant="outlined"
                            startIcon={<CloudOutlinedIcon />}
                            onClick={() => setIsLinkOneDriveOpen(true)}
                        >
                            Link OneDrive
                        </Button>
                        <Button variant="outlined" startIcon={<CreateNewFolderIcon />} onClick={() => setIsNewFolderModalOpen(true)}>
                            New Folder
                        </Button>
                        <input type="file" multiple ref={fileInputRef} style={{ display: "none" }} onChange={handleFileUpload} />
                        <Button variant="contained" startIcon={<CloudUploadIcon />} onClick={() => fileInputRef.current?.click()} disabled={isUploading}>
                            {isUploading ? "Uploading..." : "Upload File"}
                        </Button>
                    </Stack>
                </Box>

                {/* UPLOAD PROGRESS BAR */}
                {isUploading && (
                    <Box sx={{ flexShrink: 0 }}>
                        <LinearProgress variant="indeterminate" />
                    </Box>
                )}

                {/* TABLE */}
                <Paper sx={{ width: "100%", flexGrow: 1, display: "flex", flexDirection: "column", overflow: "hidden", minHeight: 0 }}>
                    {isLoading ? (
                        <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", flexGrow: 1 }}>
                            <CircularProgress />
                        </Box>
                    ) : (
                        <FileTable
                            items={items}
                            onNavigate={navigateToFolder}
                            onRefresh={() => fetchFolderData(currentFolderId)}
                            onAction={handleItemAction}
                            onItemClick={(item) => {
                                if (item.isFolder) navigateToFolder(item._id);
                                else { setPreviewItem(item); setIsPreviewModalOpen(true); }
                            }}
                        />
                    )}
                </Paper>

                {/* NEW FOLDER MODAL */}
                <Dialog
                    open={isNewFolderModalOpen}
                    onClose={() => { if (!isCreatingFolder) { setIsNewFolderModalOpen(false); setNewFolderName(""); setNewFolderNameError(null); } }}
                    PaperProps={{ sx: { minWidth: 420 } }}
                >
                    <DialogTitle sx={{ pt: 3, pb: 1.5, px: 3 }}>
                        <Typography variant="h6" sx={{ fontWeight: 700 }}>Create New Folder</Typography>
                        <Typography variant="caption" color="text.secondary">Organize your files with a new folder</Typography>
                    </DialogTitle>
                    <Divider />
                    <DialogContent sx={{ pt: 3, pb: 1, px: 3 }}>
                        <TextField
                            autoFocus fullWidth label="Folder Name" variant="outlined"
                            value={newFolderName}
                            onChange={(e) => {
                                const val = e.target.value;
                                setNewFolderName(val);
                                const exists = items.some(i => i.isFolder && i.name.toLowerCase() === val.trim().toLowerCase());
                                setNewFolderNameError(exists ? `A folder named "${val.trim()}" already exists here.` : null);
                            }}
                            onKeyDown={(e) => { if (e.key === "Enter" && newFolderName.trim() && !newFolderNameError && !isCreatingFolder) handleCreateFolder(); }}
                            disabled={isCreatingFolder}
                            placeholder="e.g. Project Documents"
                            error={!!newFolderNameError}
                            helperText={newFolderNameError || " "}
                        />
                    </DialogContent>
                    <DialogActions sx={{ px: 3, pb: 3, pt: 2, gap: 1 }}>
                        <Button onClick={() => { setIsNewFolderModalOpen(false); setNewFolderName(""); setNewFolderNameError(null); }} disabled={isCreatingFolder}>Cancel</Button>
                        <Button
                            onClick={handleCreateFolder} variant="contained"
                            disabled={!newFolderName.trim() || !!newFolderNameError || isCreatingFolder}
                            sx={{ minWidth: 140 }}
                            startIcon={isCreatingFolder ? <CircularProgress size={16} color="inherit" /> : <CreateNewFolderIcon />}
                        >
                            {isCreatingFolder ? "Creating..." : "Create Folder"}
                        </Button>
                    </DialogActions>
                </Dialog>

                {/* RENAME MODAL */}
                <Dialog
                    open={isRenameModalOpen}
                    onClose={() => !isRenamingItem && setIsRenameModalOpen(false)}
                    PaperProps={{ sx: { minWidth: 420 } }}
                >
                    <DialogTitle sx={{ pt: 3, pb: 1.5, px: 3 }}>
                        <Typography variant="h6" sx={{ fontWeight: 700 }}>Rename</Typography>
                        <Typography variant="caption" color="text.secondary" noWrap sx={{ maxWidth: 360, display: "block" }}>
                            {selectedItem?.name}
                        </Typography>
                    </DialogTitle>
                    <Divider />
                    <DialogContent sx={{ pt: 3, pb: 1, px: 3 }}>
                        <TextField
                            autoFocus fullWidth label="New Name" variant="outlined"
                            value={renameValue}
                            onChange={(e) => setRenameValue(e.target.value)}
                            onKeyDown={(e) => { if (e.key === "Enter" && renameValue.trim() && renameValue !== selectedItem?.name && !isRenamingItem) executeRename(); }}
                            disabled={isRenamingItem}
                        />
                    </DialogContent>
                    <DialogActions sx={{ px: 3, pb: 3, pt: 2, gap: 1 }}>
                        <Button onClick={() => setIsRenameModalOpen(false)} disabled={isRenamingItem}>Cancel</Button>
                        <Button
                            onClick={executeRename} variant="contained"
                            disabled={!renameValue.trim() || renameValue === selectedItem?.name || isRenamingItem}
                            sx={{ minWidth: 120 }}
                            startIcon={isRenamingItem ? <CircularProgress size={16} color="inherit" /> : <DriveFileRenameOutlineIcon />}
                        >
                            {isRenamingItem ? "Renaming..." : "Rename"}
                        </Button>
                    </DialogActions>
                </Dialog>

                <LinkOneDriveFolderDialog
                    open={isLinkOneDriveOpen}
                    onClose={() => setIsLinkOneDriveOpen(false)}
                    clientId={folderClientId}
                    /* Where the link lands when no client is chosen. The root
                       is virtual, so it passes the same sentinel the uploader
                       uses rather than a folder id. */
                    parentId={currentFolderId === "org-true-root" ? "org-true-root" : currentFolder?._id}
                    onLinked={() => fetchFolderData(currentFolderId)}
                />

                {/* MOVE MODAL */}
                <MoveItemModal
                    isOpen={isMoveModalOpen}
                    onClose={() => setIsMoveModalOpen(false)}
                    onMove={executeMove}
                    clientId=""
                    selectedItem={selectedItem}
                />

                {/* FILE PREVIEW MODAL */}
                <FilePreviewModal
                    isOpen={isPreviewModalOpen}
                    onClose={() => { setIsPreviewModalOpen(false); setPreviewItem(null); }}
                    item={previewItem}
                />

                <ConfirmDeleteModal
                    open={!!deleteTarget}
                    entityName={deleteTarget?.name ?? ""}
                    description={
                        deleteTarget ? (
                            <>
                                Do you really want to delete <strong>{deleteTarget.name}</strong>? 
                                {/* This{" "}
                                {deleteTarget.isFolder ? "folder" : "file"}  */}
                            </>
                        ) : undefined
                    }
                    onConfirm={() => executeDelete(deleteTarget)}
                    onCancel={() => setDeleteTarget(null)}
                />

                {/* DUPLICATE FILE CONFIRMATION */}
                <Dialog
                    open={isDuplicateDialogOpen}
                    onClose={() => setIsDuplicateDialogOpen(false)}
                    PaperProps={{ sx: { minWidth: 420 } }}
                >
                    <DialogTitle sx={{ pt: 3, pb: 1.5, px: 3 }}>
                        <Typography variant="h6" sx={{ fontWeight: 700 }}>File already exists</Typography>
                        <Typography variant="caption" color="text.secondary">
                            The following {duplicateFiles.length === 1 ? "file" : "files"} already exist in this folder:
                        </Typography>
                    </DialogTitle>
                    <Divider />
                    <DialogContent sx={{ pt: 2, pb: 1, px: 3 }}>
                        {duplicateFiles.map(f => (
                            <Typography key={f.name} variant="body2" sx={{ py: 0.5, fontWeight: 500 }}>• {f.name}</Typography>
                        ))}
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
                            Do you want to replace {duplicateFiles.length === 1 ? "it" : "them"}?
                        </Typography>
                    </DialogContent>
                    <DialogActions sx={{ px: 3, pb: 3, pt: 2, gap: 1 }}>
                        <Button onClick={() => { setIsDuplicateDialogOpen(false); setPendingFiles([]); setDuplicateFiles([]); }}>
                            Cancel
                        </Button>
                        <Button
                            variant="contained"
                            color="warning"
                            onClick={() => {
                                setIsDuplicateDialogOpen(false);
                                executeUpload(pendingFiles);
                                setPendingFiles([]);
                                setDuplicateFiles([]);
                            }}
                        >
                            Replace
                        </Button>
                    </DialogActions>
                </Dialog>

                <Snackbar
                    open={!!uploadError}
                    autoHideDuration={5000}
                    onClose={() => setUploadError(null)}
                    anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
                >
                    <Alert severity="error" onClose={() => setUploadError(null)} variant="filled">
                        {uploadError}
                    </Alert>
                </Snackbar>
            </Box>
        </Container>
    );
}
